import { createTheme } from '@mui/material/styles';

// FilmIQ Dark Theme - Matching Production Assumptions Dashboard
export const darkTheme = createTheme({
  palette: {
    mode: 'dark',
    primary: {
      main: '#4299E1', // Blue accent
      light: '#63B3ED',
      dark: '#3182CE',
    },
    secondary: {
      main: '#48BB78', // Green accent
      light: '#68D391',
      dark: '#38A169',
    },
    error: {
      main: '#F56565', // Red for negative values
      light: '#FC8181',
      dark: '#E53E3E',
    },
    warning: {
      main: '#ED8936', // Orange for warnings
      light: '#F6AD55',
      dark: '#DD6B20',
    },
    success: {
      main: '#48BB78', // Green for positive values
      light: '#68D391',
      dark: '#38A169',
    },
    background: {
      default: '#1A202C', // Main dark background
      paper: '#2D3748', // Card backgrounds
    },
    text: {
      primary: '#FFFFFF', // Main text
      secondary: '#A0AEC0', // Secondary text
    },
    divider: '#4A5568',
  },
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    h1: {
      fontSize: '2.5rem',
      fontWeight: 700,
      color: '#FFFFFF',
    },
    h2: {
      fontSize: '2rem',
      fontWeight: 600,
      color: '#FFFFFF',
    },
    h3: {
      fontSize: '1.5rem',
      fontWeight: 600,
      color: '#FFFFFF',
    },
    h4: {
      fontSize: '1.25rem',
      fontWeight: 600,
      color: '#FFFFFF',
    },
    h5: {
      fontSize: '1.125rem',
      fontWeight: 600,
      color: '#FFFFFF',
    },
    h6: {
      fontSize: '1rem',
      fontWeight: 600,
      color: '#FFFFFF',
    },
    body1: {
      fontSize: '1rem',
      color: '#A0AEC0',
    },
    body2: {
      fontSize: '0.875rem',
      color: '#A0AEC0',
    },
    caption: {
      fontSize: '0.75rem',
      color: '#718096',
    },
  },
  components: {
    MuiCard: {
      styleOverrides: {
        root: {
          backgroundColor: '#2D3748',
          borderRadius: '12px',
          border: '1px solid #4A5568',
          boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
        },
      },
    },
    MuiCardHeader: {
      styleOverrides: {
        root: {
          padding: '16px 20px 8px 20px',
          borderBottom: '1px solid #4A5568',
        },
        title: {
          fontSize: '1.125rem',
          fontWeight: 600,
          color: '#FFFFFF',
        },
        subheader: {
          fontSize: '0.875rem',
          color: '#A0AEC0',
        },
      },
    },
    MuiCardContent: {
      styleOverrides: {
        root: {
          padding: '16px 20px',
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: '8px',
          textTransform: 'none',
          fontWeight: 500,
        },
        contained: {
          boxShadow: 'none',
          '&:hover': {
            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: '6px',
          fontWeight: 500,
        },
        colorPrimary: {
          backgroundColor: '#4299E1',
          color: '#FFFFFF',
        },
        colorSecondary: {
          backgroundColor: '#48BB78',
          color: '#FFFFFF',
        },
        colorError: {
          backgroundColor: '#F56565',
          color: '#FFFFFF',
        },
        colorWarning: {
          backgroundColor: '#ED8936',
          color: '#FFFFFF',
        },
      },
    },
    MuiTable: {
      styleOverrides: {
        root: {
          backgroundColor: 'transparent',
        },
      },
    },
    MuiTableHead: {
      styleOverrides: {
        root: {
          backgroundColor: '#4A5568',
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          borderBottom: '1px solid #4A5568',
          color: '#A0AEC0',
        },
        head: {
          color: '#FFFFFF',
          fontWeight: 600,
          fontSize: '0.875rem',
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        root: {
          borderBottom: '1px solid #4A5568',
        },
        indicator: {
          backgroundColor: '#4299E1',
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          color: '#A0AEC0',
          fontWeight: 500,
          '&.Mui-selected': {
            color: '#4299E1',
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundColor: '#2D3748',
          color: '#FFFFFF',
        },
      },
    },
  },
});

// Widget-specific theme extensions
export const widgetTheme = {
  colors: {
    status: {
      ahead: '#48BB78',
      onTrack: '#4299E1',
      behind: '#ED8936',
      critical: '#F56565',
    },
    schedule: {
      original: '#4299E1',
      blue: '#3182CE',
      goldenrod: '#D69E2E',
    },
  },
  spacing: {
    widgetPadding: '16px',
    widgetGap: '16px',
    cardPadding: '20px',
  },
  borderRadius: {
    widget: '12px',
    card: '8px',
    button: '6px',
  },
  shadows: {
    widget: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
    widgetHover: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
  },
};
